import React, { useState, useEffect } from "react";
import Modal from "../../components/Modal";
import { DepartmentFormData } from "../../interfaces/departments";
import { createDepartment, updateDepartment } from "../../apis/departments";
import { getBusinessUnits } from "../../apis/businessUnits";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editableId?: number | null;
  departmentData?: DepartmentFormData | null;
}

const AddDepartmentModal: React.FC<Props> = ({
  open,
  onClose,
  onSuccess,
  editableId,
  departmentData,
}) => {
  const defaultDepartment: DepartmentFormData = {
    name: "",
    businessUnitId: null,
    isPharmacyBusinessUnit: false,
    isLabBusinessUnit: false,
  };

  const [department, setDepartment] =
    useState<DepartmentFormData>(defaultDepartment);
  const [businessUnits, setBusinessUnits] = useState<any[]>([]);
  const [errors, setErrors] = useState<{ name?: string; businessUnitId?: string }>({});
  const isEditMode = Boolean(editableId);

  const generateDepartmentCode = (name: string): string => {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")     // space -> underscore
    .replace(/[^\w_]/g, "");  // special characters remove
};

  useEffect(() => {
    if (open) {
      if (isEditMode && departmentData) {
        setDepartment(departmentData);
      } else {
        setDepartment(defaultDepartment);
      }
      fetchBusinessUnits();
    }
  }, [open, editableId, departmentData]);

  const fetchBusinessUnits = async () => {
    try {
      const res: any = await getBusinessUnits({ per_page: 1000 });
      setBusinessUnits(res?.data?.data || res?.data || res || []);
    } catch (error) {
      console.log("Error fetching business units:", error);
    }
  };

  const validateForm = () => {
    const e: typeof errors = {};
    if (!department.name.trim()) {
      e.name = "* Name is required";
    }
    if (!department.businessUnitId) {
      e.businessUnitId = "* Business Unit mapping is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

  const payload = {
    ...department,
    code: generateDepartmentCode(department.name),
  };
  try {
    if (isEditMode) {
      await updateDepartment(editableId!, payload);
    } else {
      await createDepartment(payload);
    }

    onSuccess();
    onClose();
  } catch (error) {
    console.log("Error saving department:", error);
  }
};

  if (!open) return null;

  return (
    <Modal
      title={isEditMode ? "Edit Department" : "Add Department"}
      onClose={onClose}
      modalContent={
        <div className="space-y-4">
          <div>
            <label>Name</label>
            <input
              type="text"
              value={department.name}
              onChange={(e) =>
                setDepartment({ ...department, name: e.target.value })
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            />
            {errors.name && (
              <span className="text-sm text-red-500">
                {errors.name}
              </span>
            )}
          </div>

          <div>
            <label>Business Unit</label>
            <select
              value={department.businessUnitId ?? ""}
              onChange={(e) =>
                setDepartment({
                  ...department,
                  businessUnitId: e.target.value ? Number(e.target.value) : null,
                })
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
            >
              <option value="">Select Business Unit</option>
              {businessUnits.map((bu: any) => (
                <option key={bu.id} value={bu.id}>
                  {bu.name}
                </option>
              ))}
            </select>
            {errors.businessUnitId && (
              <span className="text-sm text-red-500">{errors.businessUnitId}</span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <input
              id="dept-pharmacy-bu"
              type="checkbox"
              checked={!!department.isPharmacyBusinessUnit}
              onChange={(e) =>
                setDepartment({
                  ...department,
                  isPharmacyBusinessUnit: e.target.checked,
                })
              }
              className="rounded border-gray-300"
            />
            <label htmlFor="dept-pharmacy-bu">Mark as Pharmacy Business Unit</label>
          </div>

          <div className="flex items-center gap-3">
            <input
              id="dept-lab-bu"
              type="checkbox"
              checked={!!department.isLabBusinessUnit}
              onChange={(e) =>
                setDepartment({
                  ...department,
                  isLabBusinessUnit: e.target.checked,
                })
              }
              className="rounded border-gray-300"
            />
            <label htmlFor="dept-lab-bu">Mark as Lab Business Unit</label>
          </div>

          <div className="flex justify-end gap-2">
            <button
              onClick={handleSave}
              className="px-3 py-2 bg-primary text-white rounded"
            >
              {isEditMode ? "Update" : "Save"}
            </button>

            <button
              onClick={onClose}
              className="px-3 py-2 border rounded"
            >
              Cancel
            </button>

          </div>
        </div>
      }
    />
  );
};

export default AddDepartmentModal;
